import { useMemo } from 'react';

import { useQuery } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';

import { DEFAULT_RADIUS } from 'src/components/InfiniteJobList/useQueryJobsCount';

export type JobByIndexQueryResponse = {
  search?: {
    items?: [
      {
        id?: string;
        shortId?: string;
        caption?: string;
        city?: string;
        postalCode?: string;
        weeklyHours?: number;
        salary?: {
          grossHourlyRateIncludingCP?: number;
        };
      }
    ];
  };
};

export const JobByIndexQuery = gql`
  query JobByIndexQuery($skip: Int!, $text: String, $location: LocationArgs) {
    search(take: 1, skip: $skip, text: $text, filters: { location: $location }) {
      items {
        id
        shortId
        caption
        city
        weeklyHours
        postalCode
        salary {
          grossHourlyRateIncludingCP
        }
      }
    }
  }
`;

type Options = {
  skip?: boolean;
  onCompleted?: () => void;
  onError?: () => void;
};

const useQueryJobByIndex = (
  index?: number,
  query?: string,
  lat?: number,
  lng?: number,
  radius = DEFAULT_RADIUS,
  options: Options = {}
) => {
  const variables = useMemo(() => {
    if (lat && lng) {
      return {
        skip: index,
        text: query || undefined,
        location: {
          radius,
          latitude: lat,
          longitude: lng,
        },
      };
    }
    return { skip: index, text: query || undefined };
  }, [index, query, lat, lng, radius]);

  const { data, error, loading } = useQuery<JobByIndexQueryResponse>(JobByIndexQuery, {
    variables,
    fetchPolicy: 'cache-first',
    ...options,
  });

  const job = useMemo(() => {
    return data?.search?.items?.[0];
  }, [data]);

  return { job, error, loading };
};

export default useQueryJobByIndex;
